
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { API_ROUTES } from "@/utils/API";
import { API_TReport } from "@/models/report";
import { SMCases, SMUser } from "@/store";
import {
  REPORT_CORRECTION_SERVICE_STATUS,
  SKILLED_WORKER_COS_INSIDE_SERVICE_TYPE,
  SKILLED_WORKER_COS_SERVICE_TYPE,
  SKILLED_WORKER_SERVICE_TYPE,
  SPONSOR_LICENCE_SERVICE_TYPE,
  CANDIDATE_ASSESSMENT_SERVICE_TYPE,
} from "@/constants/services";
import { API_TCase } from "@/models/cases";

const REPORT_PARSE_TYPE = "html";
const REPORT_DOWNLOAD_TYPE = "pdf";

const EDITOR_PARAMS = {
  outputType: "html",
  dataControls: false,
};

@Component({
  components: {
    Loader: () => import("@/components/Loader/Loader.vue"),
    WidgetReportGenerated: () => import("./WidgetReportGenerated.vue"),
    ActionButtons: () => import("@/components/ActionContainer/ActionButtons.vue"),
    NotificationCosPlusSwStarted: () =>
      import("@/components/Notifications/NotificationCosPlusSwStarted.vue"),
    NotificationSlStarted: () => import("@/components/Notifications/NotificationSlStarted.vue"),
    NotificationServiceEmployeeStarted: () =>
      import("@/components/Notifications/NotificationServiceEmployeeStarted.vue"),
  },
})
export default class VCandidateTemplateReport extends Vue {
  @Prop({ type: Boolean, default: false }) readonly isEditable: boolean | undefined;

  loading = false;
  init = false;

  dialogs = {
    changeDetails: false,
  };

  mounted() {
    this.fetchServiceReport();
  }

  get serviceId(): number {
    return Number(this.$route.params.id ?? 0);
  }

  get childServiceId(): number {
    const service = SMCases.case as API_TCase;

    return Number(service?.child_case_id ?? 0);
  }

  get alias(): string {
    const { query, params } = this.$route;

    return (query?.report_template_alias || params?.report_template_alias || "") as string;
  }

  @Watch("serviceId")
  serviceIdChanged() {
    this.fetchServiceReport();
  }

  @Watch("childServiceId", { immediate: true })
  childServiceIdChanged(val: boolean) {
    if (!val) return;

    this.fetchChildService();
  }

  childService = {};

  get reportId(): number {
    return Number(this.$route.params.tabTypeId ?? 0);
  }

  @Watch("reportId")
  reportIdChanged() {
    this.fetchServiceReport();
  }

  get isActionsHidden() {
    return SMUser.isExpert;
  }

  report: API_TReport = {
    content: "",
    eligibility_result: "",
    user_name: "",
    widget_type: "",
    has_child_case: false,
    is_sl_started: false,
    if_sponsor: false,
  };

  get reportStatus() {
    return this.report.eligibility_result;
  }

  get showReportWidget() {
    return this.report.eligibility_result && this.reportStatus;
  }

  get isServiceEmployeeStarted() {
    return SMUser.isEmployee && !SMCases.isExtension;
  }

  get isCosPlusSwStarted() {
    if (!this.childServiceId) return false;

    const service = this.childService as API_TCase;

    if (!service) return false;

    return (
      this.report.has_child_case &&
      [
        SKILLED_WORKER_SERVICE_TYPE,
        SKILLED_WORKER_COS_SERVICE_TYPE,
        SKILLED_WORKER_COS_INSIDE_SERVICE_TYPE,
      ].includes(service.case_template?.case_type)
    );
  }

  get isSlStarted() {
    if (!this.childServiceId) return false;

    const service = this.childService as API_TCase;

    if (!service) return false;

    return (
      this.report.has_child_case &&
      service.case_template?.case_type === SPONSOR_LICENCE_SERVICE_TYPE
    );
  }

  get isCandidateAssessment() {
    return SMCases.caseType === CANDIDATE_ASSESSMENT_SERVICE_TYPE;
  }

  get isArchived(): boolean {
    return SMCases.isArchived && this.isCandidateAssessment && !this.isCosPlusSwStarted;
  }

  get isChangeDetailsDisabled() {
    if (this.isArchived) return true;

    return !!this.childServiceId && SMUser.isEmployee;
  }

  reportLoading = false;

  async fetchServiceReport() {
    const { serviceId, reportId } = this;

    if (!serviceId || !reportId) return;

    try {
      this.reportLoading = true;

      const url = API_ROUTES.CASE_REPORT_PARSE(this.serviceId, this.alias, REPORT_PARSE_TYPE);

      const { data } = await this.axios.get(url);

      if (!data?.content) return;

      this.report = { ...data };
    } finally {
      this.reportLoading = false;
      this.init = true;
    }
  }

  generatePDFFile(response: any): void {
    const { job_title, first_name, last_name } = SMCases.mainApplicant;

    const blob = new Blob([response.data], { type: "application/pdf" });
    const link = document.createElement("a");

    link.href = window.URL.createObjectURL(blob);
    link.download = `Eligibility_Check_${job_title}_${first_name}_${last_name}.pdf`;
    link.click();
  }

  async download() {
    this.loading = true;

    const { serviceId, reportId } = this;

    if (!serviceId || !reportId) return;

    try {
      const url = API_ROUTES.CASE_REPORT_PARSE(this.serviceId, this.alias, REPORT_DOWNLOAD_TYPE);

      const response = (await this.axios.get(url, {
        responseType: "arraybuffer",
      })) as any;

      if (!response) return;

      this.generatePDFFile(response);
    } finally {
      this.loading = false;
    }
  }

  openChangeDetailsPopup(): void {
    this.dialogs.changeDetails = true;
  }

  closeChangeDetailsPopup(): void {
    this.dialogs.changeDetails = false;
  }

  async change() {
    const { serviceId } = this;

    if (!serviceId) return;

    try {
      this.loading = true;

      const url = API_ROUTES.CASE_REPORT_CHANGE(this.serviceId);

      await this.axios.get(url);
    } finally {
      SMCases.setCaseStatus(REPORT_CORRECTION_SERVICE_STATUS);

      this.closeChangeDetailsPopup();

      this.loading = false;
    }
  }

  editing = false;
  editorParams = EDITOR_PARAMS;

  edit(): void {
    this.editing = true;
  }

  cancel(): void {
    this.editing = false;
  }

  convertHtmlToBlob(data: string): FormData {
    const formData = new FormData();

    formData.append("content", data);

    return formData;
  }

  submitSave(): void {
    const editorInstance = this.$refs.editorInstance as Vue & { save: () => {} };

    editorInstance.save && editorInstance.save();
  }

  async save(payload: { html: string }) {
    try {
      this.loading = true;

      this.report.content = payload.html;

      await this.$http.put(
        API_ROUTES.CASE_REPORT_EDIT(this.serviceId, this.alias),
        this.convertHtmlToBlob(payload.html)
      );
    } finally {
      this.loading = false;

      this.cancel();
    }
  }

  async fetchChildService() {
    const id = this.childServiceId;

    if (!id) return;

    const { data } = await this.$http.get(API_ROUTES.CASE_ID(id));

    if (!data) return;

    this.childService = data;
  }
}
